import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isError } from 'lodash';
import { type ReactStripeElements } from 'react-stripe-elements';

import { useTrackChangedPaymentMethod } from '@air/analytics';
import { Subscriptions } from '@air/api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { WORKSPACE_LOCKED_PAYMENT_UPDATED } from '~/constants/localStorageKeys';
import { getSubscriptionKey } from './useSubscription';
import { getSubscriptionPlanKey } from './useSubscriptionPlan';

type UpdatePaymentMethodParams = {
  stripe: ReactStripeElements.InjectedStripeProps['stripe'];
  workspaceId: string;
};

export function useUpdatePaymentMethodMutation() {
  const queryClient = useQueryClient();
  const { trackChangedPaymentMethod } = useTrackChangedPaymentMethod();

  return useMutation<void, Error, UpdatePaymentMethodParams>({
    mutationFn: async ({ stripe, workspaceId }) => {
      if (!stripe) {
        throw new Error('Payment system is still loading. Please try again.');
      }

      if (!workspaceId) {
        throw new Error('No workspace selected. Please refresh and try again.');
      }

      try {
        const { token, error } = await stripe.createToken({});

        if (error) {
          throw new Error(error.message);
        }

        if (isError(token) || !token) {
          throw new Error(typeof token === 'string' ? token : 'A token failed to be created');
        }

        await Subscriptions.updateStripeSubscriptionPaymentMethod({ workspaceId, token });

        // Track analytics
        trackChangedPaymentMethod();

        // Store payment update timestamp with workspace ID
        const storageKey = `${WORKSPACE_LOCKED_PAYMENT_UPDATED}/${workspaceId}`;
        localStorage.setItem(storageKey, new Date().toISOString());
      } catch (error) {
        const reportedError = reportErrorToBugsnag({
          error,
          context: `Failed to update payment method`,
        });
        throw new Error(reportedError.message);
      }
    },
    onSuccess: (_data, { workspaceId }) => {
      // Invalidate subscription queries
      queryClient.invalidateQueries({ queryKey: getSubscriptionKey(workspaceId) });
      queryClient.invalidateQueries({ queryKey: getSubscriptionPlanKey(workspaceId) });
    },
  });
}
